/**
 * Shared time utility functions used across multiple controllers
 */
import { BUSINESS_HOURS } from "../constants/TimeConstants";

/**
 * Creates a Date object from date string in dd-MM-yyyy format
 * @param dateStr Date string like "04-10-2025"
 * @returns Date object at noon local time
 */
export function parseDatePickerValue(dateStr: string): Date {
    const [day, month, year] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day, 12, 0, 0, 0);
}

/**
 * Creates start of day Date (00:00:00.000)
 * @param date Base date
 * @returns Date at start of day
 */
export function getStartOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

/**
 * Creates end of day Date (23:59:59.999)
 * @param date Base date
 * @returns Date at end of day
 */
export function getEndOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
}

/**
 * Gets ISO date string (YYYY-MM-DD) from Date object
 * @param date Date object
 * @returns ISO date string
 */
export function getISODateString(date: Date): string {
    return date.toISOString().split("T")[0];
}

/**
 * Gets the maximum date for booking (exactly 4 weeks from today)
 * @returns Date object representing the maximum bookable date
 */
export function getMaxBookingDate(): Date {
    const today = new Date();
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + 28); // Exactly 4 weeks (28 days)
    return maxDate;
}

/**
 * Parses a time string (HH:mm) to total minutes since midnight
 */
export function parseTimeToMinutes(timeString: string): number {
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 60 + (minutes || 0);
}

/**
 * Compares two time strings and returns true if time1 >= time2
 */
export function isTimeGreaterOrEqual(time1: string, time2: string): boolean {
    const [hours1, minutes1] = time1.split(":").map(Number);
    const [hours2, minutes2] = time2.split(":").map(Number);

    const totalMinutes1 = hours1 * 60 + minutes1;
    const totalMinutes2 = hours2 * 60 + minutes2;

    return totalMinutes1 >= totalMinutes2;
}

/**
 * Gets current time plus specified minutes in HH:mm format
 */
export function getCurrentTimePlusMinutes(minutes: number = 1): string {
    const now = new Date();
    now.setMinutes(now.getMinutes() + minutes);
    const hours = now.getHours();
    const mins = now.getMinutes();
    return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}

/**
 * Calculates end time as start time + specified hours
 */
export function calculateEndTimeFromStart(startTime: string, hoursToAdd: number = 1): string {
    const [hours, minutes] = startTime.split(":").map(Number);
    const endHour = Math.min(hours + hoursToAdd, 23); // Don't exceed 23:59
    return `${endHour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}

/**
 * Validates and adjusts start time based on current time if booking is for today
 */
export function validateAndAdjustStartTime(startTime: string, selectedDate: Date): string {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const bookingDate = new Date(selectedDate);
    bookingDate.setHours(0, 0, 0, 0);

    // If booking is for today, ensure start time is not in the past
    if (bookingDate.getTime() === today.getTime()) {
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        const startMinutes = parseTimeToMinutes(startTime);

        if (startMinutes <= currentMinutes) {
            return getCurrentTimePlusMinutes(1);
        }
    }

    return startTime;
}

/**
 * Checks if flexible time should be used based on current time and date
 */
export function shouldUseFlexibleTime(selectedDate: Date, isConferenceRoom: boolean): boolean {
    if (isConferenceRoom) {
        return true;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const bookingDate = new Date(selectedDate);
    bookingDate.setHours(0, 0, 0, 0);

    if (bookingDate.getTime() === today.getTime()) {
        // Only after business hours (17:00)
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        const businessEnd = parseTimeToMinutes(BUSINESS_HOURS.END);
        return currentMinutes >= businessEnd;
    }

    return false;
}
